import { useMemo, useState } from 'react';

import moment from 'moment-timezone';

import { useBattleCardsStore } from 'store/battlecards.store';

import { Payload } from 'api/endPoints/getPivotTable';
import { useMutationExtractPivotTableToCsv } from 'api/mutations/extractPivotTableToCsv.mutation';
import { useQueryGetPivotTable } from 'api/queries/getPivotTable.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { DisplayStyle } from 'components/common/ToggleView/ToggleView';

import 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/PivotBoard/PivotBoard.types';
import { createSpecialFiltersPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';

export type UseGetPivotTableProvider = ReturnType<typeof useGetPivotTableProvider>;

const INITIAL_PRIMARY_GROUP_SELECT = { value: 'wbsLevel4', label: 'WBS Level 4' };

interface Props {
    latestVersionId?: number;
    projectId?: number | null;
    contractId?: number | null;
    getPivotTableQueryEndpoint: string;
    globalFilterData: any;
}

export const useGetPivotTableProvider = ({
    latestVersionId,
    getPivotTableQueryEndpoint,
    globalFilterData,
    projectId,
    contractId,
}: Props) => {
    let filters = useMemo(() => {
        const globalFiltersIsEmpty = globalFilterData ? Object.keys(globalFilterData)?.length === 0 : true;
        if (!projectId || !contractId || !latestVersionId || globalFiltersIsEmpty)
            return { filtersArray: [], flags: {} };

        const baseFilters = getFiltersData({
            [FilterNames.PROJECT_CARD_BOARD]: [projectId],
            [FilterNames.CONTRACT_CARD_BOARD]: [contractId],
            [FilterNames.VERSION_CARD_BOARD]: [latestVersionId],
            [FilterNames.ACTIVITY_TYPE_BOARD]: globalFilterData?.cardActivity,
            [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
            [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                ?.filter((item) => !item.isExclude)
                .map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.ASSIGNEE_CARD_BOARD]: globalFilterData?.assignee,
            [FilterNames.IMPORTANT_CARD_BOARD]: globalFilterData?.important,
            [FilterNames.URGENT_TYPE_CARD_BOARD]: globalFilterData?.urgent,
            [FilterNames.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD]: globalFilterData?.completionPlanSegment,
            [FilterNames.DOCLINK_CARD_BOARD]: globalFilterData?.hasAttachments,
            [FilterNames.LAG_CARD_BOARD]: globalFilterData?.lagLead,
            [FilterNames.TRACKED_CARD_BOARD]: globalFilterData?.trackedActivity,
            [FilterNames.FRAGNET_FORWARD_SEARCH]: globalFilterData?.activityForwardFragnet,
            [FilterNames.TYPE_CARD_BOARD]: globalFilterData?.cardType,
            [FilterNames.CLUSTER_CARD_BOARD]: globalFilterData?.cluster,
            [FilterNames.DURATION_CARD_BOARD]: globalFilterData?.duration,
            [FilterNames.TOTAL_FLOAT_CARD_BOARD]: globalFilterData?.float,
            [FilterNames.ACTIVITY_NAME_CARD_BOARD]: globalFilterData?.relatedActivity,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: globalFilterData?.resourceName,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: globalFilterData?.resourceType,
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: globalFilterData?.submittedCompletion,
            [FilterNames.UPDATED_USER_CARD_BOARD]: globalFilterData?.updatedBy,
            [FilterNames.WBS_LEVEL_CARD_BOARD]: globalFilterData?.wbsLevel,
            [FilterNames.STATUS_CARD_BOARD]: globalFilterData.status,
        });

        const specialFiltersPayload = createSpecialFiltersPayload(globalFilterData);
        const specialFilters = specialFiltersPayload?.filters ?? [];
        const flags = specialFiltersPayload?.flags ?? {};

        return { filtersArray: [...baseFilters, ...specialFilters], flags };
    }, [projectId, contractId, latestVersionId, globalFilterData]);

    const displayStyle = useBattleCardsStore((state) => state.displayStyle);

    const [primaryGroupSelected, setPrimaryGroupSelected] =
        useState<ISelectOption<string>>(INITIAL_PRIMARY_GROUP_SELECT);
    const [secondaryGroupSelected, setSecondaryGroupSelected] = useState<ISelectOption<string> | null>(null);

    const getPivotTableDataPayload = (): Payload => {
        return {
            cardCachedFilter: {
                filter: {
                    filters: filters.filtersArray,
                    flags: filters.flags,
                },
            },
            paging: { size: 100, page: 0 },
            primaryGroupField: primaryGroupSelected.value,
            secondaryGroupField: secondaryGroupSelected?.value ?? null,
            currentDay: moment().local().format('YYYY-MM-DD'),
        };
    };

    const payload = getPivotTableDataPayload();
    const isPivotTableViewSelected = displayStyle === DisplayStyle.PIVOT;
    const getPivotTableQuery = useQueryGetPivotTable(payload, getPivotTableQueryEndpoint, isPivotTableViewSelected);

    const getState = () => {
        return {
            primaryGroupSelected,
            secondaryGroupSelected,
            isPivotTableViewSelected: isPivotTableViewSelected,
            isGroupByTableLoading: getPivotTableQuery.isLoading,
            groupedByTableFlatData: getPivotTableQuery.data,
        };
    };

    return {
        ui: {
            state: getState(),
            setters: {
                setPrimaryGroupSelected,
                setSecondaryGroupSelected,
            },
        },
        api: {
            getPivotTable: {
                instance: getPivotTableQuery,
                payload,
            },
            exportPivotTableToCsv: {
                instance: useMutationExtractPivotTableToCsv(),
                payload,
            },
        },
    };
};
